/**
 * ConstructionHubWorkspace — Construction Hub, the companion to Permitting Hub (2026-10, Matt).
 * Four tabs, all cross-project and read + navigate only: Open Items, Contracts & Portals,
 * Contractors, Intake. Every edit still happens in the project's own Construction section.
 * See docs/CONSTRUCTION_HUB.md.
 *
 * Mirrors PermittingWorkspace.jsx's shell exactly: docks beside SiteNav (`.aj-construction-ws`
 * in outline.css), `ws=construction` + `wsTool`, sessionStorage/localStorage persistence,
 * `open-construction-hub`/`close-construction-hub` events. No module gate — everyone signed in.
 */
import { useEffect, useState } from 'react'
import { isEmbedded } from '../outline/state.js'
import { WorkspaceHeader, workspaceTabsWrapStyle, workspaceTabStyle } from './WorkspaceChrome.jsx'
import { OpenItemsTab, ContractsTab, ContractorsTab, IntakeTab } from './ConstructionHubTabs.jsx'

const STORE_KEY = 'aj:construction:ws'
const TABS = ['open', 'contracts', 'contractors', 'intake']
const TAB_LABEL = { open: 'Open Items', contracts: 'Contracts & Portals', contractors: 'Contractors', intake: 'Intake' }

function readStored() {
  try {
    const params = new URLSearchParams(window.location.search)
    const ws = params.get('ws')
    if (ws === 'construction') return { active: true, tool: params.get('wsTool') || 'open' }
    if (ws !== null) return null
    if (isEmbedded()) return null
    const t = sessionStorage.getItem(STORE_KEY)
    if (t) return JSON.parse(t)
    return JSON.parse(localStorage.getItem(STORE_KEY) || 'null')
  } catch { return null }
}

export default function ConstructionHubWorkspace() {
  const stored = readStored()
  const [active, setActive] = useState(!!(stored && stored.active))
  const [tool, setTool] = useState(TABS.includes(stored && stored.tool) ? stored.tool : 'open')

  useEffect(() => {
    function onOpen(e) {
      const d = (e && e.detail) || {}
      if (TABS.includes(d.tool)) setTool(d.tool)
      setActive(true)
    }
    function onClose() { setActive(false) }
    window.addEventListener('open-construction-hub', onOpen)
    window.addEventListener('close-construction-hub', onClose)
    return () => { window.removeEventListener('open-construction-hub', onOpen); window.removeEventListener('close-construction-hub', onClose) }
  }, [])

  useEffect(() => {
    try {
      if (active) {
        const json = JSON.stringify({ active: true, tool })
        sessionStorage.setItem(STORE_KEY, json); localStorage.setItem(STORE_KEY, json)
      } else {
        sessionStorage.removeItem(STORE_KEY); localStorage.removeItem(STORE_KEY)
      }
    } catch { /* ignore */ }
  }, [active, tool])

  useEffect(() => {
    const url = new URL(window.location)
    if (active) {
      url.searchParams.set('ws', 'construction'); url.searchParams.set('wsTool', tool)
      document.title = 'Construction Hub — Alder James'
    } else if (url.searchParams.get('ws') === 'construction') {
      url.searchParams.delete('ws'); url.searchParams.delete('wsTool')
    }
    window.history.replaceState(null, '', url)
  }, [active, tool])

  useEffect(() => {
    if (!active) return
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [active])

  if (!active) return null

  return (
    <div className="aj-construction-ws" style={S.panel}>
      <WorkspaceHeader
        title="Construction Hub"
        tabs={(
          <div style={workspaceTabsWrapStyle}>
            {TABS.map(t => (
              <button key={t} style={workspaceTabStyle(tool === t)} onClick={() => setTool(t)}>{TAB_LABEL[t]}</button>
            ))}
          </div>
        )}
      />
      <div style={S.content}>
        {tool === 'open' && <OpenItemsTab />}
        {tool === 'contracts' && <ContractsTab />}
        {tool === 'contractors' && <ContractorsTab />}
        {tool === 'intake' && <IntakeTab />}
      </div>
    </div>
  )
}

const S = {
  panel: { display: 'flex', flexDirection: 'column', height: '100%' },
  content: { flex: 1, overflow: 'auto', background: 'var(--aj-bg-page)' },
}
